import { FC, useCallback, useEffect, useState } from 'react';
import {
  Tag,
  Button,
  InlineNotification,
  DefinitionTooltip,
} from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { PageHeader } from 'components/page-header';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { formatNumber } from 'utils/number';
import request from 'lib/request';
import { bulkDumpStatusAtom } from './atoms';
import type {
  BulkDumpBusyJob,
  BulkDumpChunk,
  BulkDumpOverview,
  BulkDumpPartition,
  BulkDumpQueueStats,
  PaginatedResponse,
} from './types';
import styles from './page.module.scss';

const Tip: FC<{ text: string; children: React.ReactNode }> = ({
  text,
  children,
}) => (
  <DefinitionTooltip definition={text} align="bottom">
    {children}
  </DefinitionTooltip>
);

const fmt = (n: number | null | undefined) => {
  if (n === null || n === undefined) return '-';
  return formatNumber(n, 0, 0, 'decimal', true);
};

const fmtElapsed = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${formatNumber(seconds / 60, 1, 1, 'decimal')}m`;
  return `${formatNumber(seconds / 3600, 1, 1, 'decimal')}h`;
};

// ── Server-side paginated data hook ─────────────────────────────────────

const CHUNKS_SORT_MAP: Record<string, string> = {
  id: 'id',
  uploadedAt: 'uploaded_at',
  verifiedAt: 'verified_at',
  yearMonth: 'year_month',
  slotIdMin: 'slot_id_min',
  slotIdMax: 'slot_id_max',
  evalIdMin: 'eval_id_min',
  evalIdMax: 'eval_id_max',
  rowCount: 'row_count',
  verificationFailureReason: 'verification_failure_reason',
  chunkPath: 'chunk_path',
};

function usePaginatedChunks(sortMap: Record<string, string>) {
  const [data, setData] = useState<BulkDumpChunk[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortColumn, setSortColumn] = useState<string | null>('uploadedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (sortColumn) {
        const backendCol = sortMap[sortColumn] ?? sortColumn;
        params.set('sortColumn', backendCol);
        params.set('sortDirection', sortDirection);
      }
      columnFilters.forEach((f) => {
        params.set(`filter[${f.id}]`, String(f.value));
      });

      const response = await request.get<PaginatedResponse<BulkDumpChunk>>(
        `/admin/bulk_dump_status/chunks?${params.toString()}`
      );
      if ('error' in response) throw new Error(response.error);
      setData(response.rows);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Fetch failed'));
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sortColumn, sortDirection, columnFilters, sortMap]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLoadData = useCallback(
    ({ page: p, pageSize: ps }: TableState) => {
      setPage(p);
      setPageSize(ps);
    },
    []
  );

  const handleSortChange = useCallback((sorting: SortingState) => {
    if (sorting.length === 0) {
      setSortColumn(null);
    } else {
      setSortColumn(sorting[0].id);
      setSortDirection(sorting[0].desc ? 'desc' : 'asc');
    }
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    setColumnFilters(filters);
    setPage(1);
  }, []);

  return {
    data,
    totalCount,
    isLoading,
    error,
    handleLoadData,
    handleSortChange,
    handleFilterChange,
    refresh: fetchData,
  };
}

// ── Overview Section ────────────────────────────────────────────────────

const OverviewSection: FC<{ overview: BulkDumpOverview }> = ({ overview }) => (
  <div className={styles.section}>
    <h4>Overview</h4>
    {overview.cutoffError && (
      <InlineNotification
        kind="warning"
        title="SLOT_ID_CUTOFF not set"
        subtitle={overview.cutoffError}
        lowContrast
        hideCloseButton
      />
    )}
    <div className={styles.configGrid}>
      <Tip text="Lowest evaluation_output_slot_id in the database — the start of the dump range">
        <Tag type="gray" size="sm">
          Floor: {fmt(overview.floor)}
        </Tag>
      </Tip>
      <Tip text="SLOT_ID_CUTOFF — highest evaluation_output_slot_id to dump, per environment">
        <Tag type="gray" size="sm">
          Cutoff: {fmt(overview.cutoff)}
        </Tag>
      </Tip>
      <Tip text="Highest slot_id_max recorded in slot_months_archive_chunks manifest">
        <Tag type="blue" size="sm">
          Reached: {fmt(overview.maxSlotIdReached)}
        </Tag>
      </Tip>
      <Tip text="Total Parquet chunks uploaded to Azure">
        <Tag type="teal" size="sm">
          Chunks: {fmt(overview.totalChunks)}
        </Tag>
      </Tip>
      <Tip text="Chunks whose verified_at has been stamped by the reader/verifier">
        <Tag type="green" size="sm">
          Verified: {fmt(overview.verifiedChunks)}
        </Tag>
      </Tip>
      <Tip text="Chunks awaiting verification — uploaded but not yet confirmed">
        <Tag
          type={overview.unverifiedChunks > 0 ? 'warm-gray' : 'gray'}
          size="sm"
        >
          Unverified: {fmt(overview.unverifiedChunks)}
        </Tag>
      </Tip>
      <Tip text="Chunks whose verification found a mismatch — see verification_failure_reason on each chunk row">
        <Tag type={overview.failedChunks > 0 ? 'red' : 'gray'} size="sm">
          Failed: {fmt(overview.failedChunks)}
        </Tag>
      </Tip>
      <Tip text="Sum of row_count across all manifest rows — approximate evaluation_output_slot_months rows archived">
        <Tag type="cyan" size="sm">
          Rows: {fmt(overview.totalRows)}
        </Tag>
      </Tip>
      <Tip text="Sum of per-partition coverage divided by total span — % of the dump range covered across all 8 partitions">
        <Tag
          type={overview.percentComplete >= 100 ? 'green' : 'blue'}
          size="sm"
        >
          Progress: {overview.percentComplete}%
        </Tag>
      </Tip>
      <Tip text="AZURE_EVAL_OUTPUT_CONTAINER — Azure Blob Storage container for archive chunks">
        <Tag type="gray" size="sm">
          Container: {overview.container}
        </Tag>
      </Tip>
    </div>
  </div>
);

const QueueSection: FC<{ queueStats: BulkDumpQueueStats }> = ({
  queueStats,
}) => (
  <div className={styles.section}>
    <h4>Sidekiq Queue (archive_bulk_dump)</h4>
    <div className={styles.configGrid}>
      <Tip text="Jobs waiting in the archive_bulk_dump Sidekiq queue">
        <Tag type="blue" size="sm">
          Queued: {fmt(queueStats.queued)}
        </Tag>
      </Tip>
      <Tip text="ArchiveSlotMonthsRangeJob instances currently executing on worker threads">
        <Tag type="cyan" size="sm">
          Busy: {fmt(queueStats.busy)}
        </Tag>
      </Tip>
      <Tip text="Jobs in the Sidekiq retry set (failed, waiting to retry)">
        <Tag type="warm-gray" size="sm">
          Retry: {fmt(queueStats.retry)}
        </Tag>
      </Tip>
      <Tip text="Jobs scheduled for future execution via self-chain">
        <Tag type="purple" size="sm">
          Scheduled: {fmt(queueStats.scheduled)}
        </Tag>
      </Tip>
    </div>
  </div>
);

// ── Progress cell ───────────────────────────────────────────────────────

const ProgressCell: FC<{ percent: number }> = ({ percent }) => (
  <div className={styles.progressCell}>
    <div className={styles.progressBar}>
      <div
        className={styles.progressFill}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
    <span className={styles.progressLabel}>{percent}%</span>
  </div>
);

// ── Partitions Table ────────────────────────────────────────────────────

const partitionColumns: DataTableColumnDef<BulkDumpPartition>[] = [
  {
    accessorKey: 'index',
    header: 'Partition',
    size: 90,
    cell: ({ getValue }) => `#${(getValue() as number) + 1}`,
  },
  {
    id: 'range',
    header: 'Slot ID Range',
    size: 220,
    cell: ({ row }) => {
      const { slotIdMin, slotIdMax } = row.original;
      return `${fmt(slotIdMin)} — ${fmt(slotIdMax)}`;
    },
  },
  {
    accessorKey: 'span',
    header: 'Span',
    size: 100,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'percentComplete',
    header: 'Progress',
    size: 180,
    cell: ({ getValue }) => <ProgressCell percent={getValue() as number} />,
  },
  {
    accessorKey: 'chunks',
    header: 'Chunks',
    size: 90,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'rows',
    header: 'Rows',
    size: 110,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'maxSlotIdReached',
    header: 'Reached',
    size: 120,
    cell: ({ getValue }) => fmt(getValue() as number | null),
  },
  {
    accessorKey: 'lastUploadedAt',
    header: 'Last Upload',
    size: 200,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleString() : '-';
    },
  },
  {
    id: 'runningJob',
    header: 'Running',
    size: 140,
    cell: ({ row }) => {
      const job = row.original.runningJob;
      if (!job)
        return (
          <Tag type="gray" size="sm">
            Idle
          </Tag>
        );
      return (
        <Tag type="green" size="sm">
          {fmtElapsed(job.elapsedSeconds)}
        </Tag>
      );
    },
  },
];

// ── Busy Jobs Table ─────────────────────────────────────────────────────

const busyColumns: DataTableColumnDef<BulkDumpBusyJob>[] = [
  {
    accessorKey: 'slotIdMin',
    header: 'Slot ID Min',
    size: 140,
    cell: ({ getValue }) => fmt(getValue() as number | null),
  },
  {
    accessorKey: 'slotIdMax',
    header: 'Slot ID Max',
    size: 140,
    cell: ({ getValue }) => fmt(getValue() as number | null),
  },
  {
    accessorKey: 'runPathSegment',
    header: 'Segment',
    size: 220,
    // Without this column, two sub-shards running on overlapping/adjacent
    // slot ranges within the same partition look identical (same min/max
    // bounds when narrow). The segment is what distinguishes them.
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? (
        <span className={styles.chunkPath}>{val}</span>
      ) : (
        '(default)'
      );
    },
  },
  {
    accessorKey: 'elapsedSeconds',
    header: 'Running For',
    size: 120,
    cell: ({ getValue }) => fmtElapsed(getValue() as number),
  },
  {
    accessorKey: 'runAt',
    header: 'Started At',
    size: 200,
    cell: ({ getValue }) => new Date(getValue() as string).toLocaleString(),
  },
  {
    accessorKey: 'process',
    header: 'Process',
    size: 200,
  },
];

// ── Chunks Table ────────────────────────────────────────────────────────

const chunkColumns: DataTableColumnDef<BulkDumpChunk>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    size: 80,
  },
  {
    accessorKey: 'yearMonth',
    header: 'Year-Month',
    size: 110,
  },
  {
    accessorKey: 'slotIdMin',
    header: 'Slot Min',
    size: 110,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'slotIdMax',
    header: 'Slot Max',
    size: 110,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'evalIdMin',
    header: 'Eval Min',
    size: 110,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'evalIdMax',
    header: 'Eval Max',
    size: 110,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    accessorKey: 'rowCount',
    header: 'Rows',
    size: 110,
    cell: ({ getValue }) => fmt(getValue() as number),
  },
  {
    id: 'verificationStatus',
    header: 'Status',
    size: 110,
    enableSorting: false,
    cell: ({ row }) => {
      const { verifiedAt, verificationFailureReason } = row.original;
      if (verificationFailureReason) {
        return (
          <Tag type="red" size="sm">
            Failed
          </Tag>
        );
      }
      if (verifiedAt) {
        return (
          <Tag type="green" size="sm">
            Verified
          </Tag>
        );
      }
      return (
        <Tag type="gray" size="sm">
          Pending
        </Tag>
      );
    },
  },
  {
    accessorKey: 'verificationFailureReason',
    header: 'Failure Reason',
    size: 300,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      if (!val) return '-';
      return <span className={styles.chunkPath}>{val}</span>;
    },
  },
  {
    accessorKey: 'uploadedAt',
    header: 'Uploaded At',
    size: 200,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleString() : '-';
    },
  },
  {
    accessorKey: 'verifiedAt',
    header: 'Verified At',
    size: 200,
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? new Date(val).toLocaleString() : '-';
    },
  },
  {
    accessorKey: 'runPathSegment',
    header: 'Segment',
    size: 220,
    // The run_path_segment encodes the dumper run that produced the chunk:
    // - "slot_X_Y" → original partition or fresh-start sub-shard
    // - "redump_slot_X_Y" → INS-8602 redump
    // After INS-8587 subdivision, a partition can have chunks under the
    // original "slot_X_Y" segment AND multiple sub-shard segments
    // ("slot_A_B" where A..B is a subset). Sorting by segment groups
    // chunks within the same dump run so operators can spot subdivisions.
    cell: ({ getValue }) => {
      const val = getValue() as string | null;
      return val ? <span className={styles.chunkPath}>{val}</span> : '-';
    },
  },
  {
    accessorKey: 'chunkPath',
    header: 'Chunk Path',
    size: 400,
    cell: ({ getValue }) => (
      <span className={styles.chunkPath}>{getValue() as string}</span>
    ),
  },
];

// ── Legends ─────────────────────────────────────────────────────────────

const PartitionsLegend: FC = () => (
  <div className={styles.legend}>
    <div className={styles.legendGrid}>
      <span className={styles.legendTerm}>Partition</span>
      <span className={styles.legendDef}>
        1-of-8 disjoint slot-id ranges that the 8 workers split the dump across.
        Assigned by <code>Archive::SlotMonthsBulkDump.partition_ranges</code>.
      </span>
      <span className={styles.legendTerm}>Slot ID Range</span>
      <span className={styles.legendDef}>
        Inclusive <code>[min, max]</code> <code>evaluation_output_slot_id</code>{' '}
        bounds for this partition. Set when the worker was enqueued; never
        changes.
      </span>
      <span className={styles.legendTerm}>Span</span>
      <span className={styles.legendDef}>
        <code>max - min + 1</code> — total slot IDs this partition is
        responsible for. All 8 partitions have equal spans except possibly the
        last.
      </span>
      <span className={styles.legendTerm}>Progress</span>
      <span className={styles.legendDef}>
        <code>(reached - min + 1) / span</code> — fraction of this
        partition&apos;s slot-id range that has a corresponding chunk row in the
        manifest.
      </span>
      <span className={styles.legendTerm}>Chunks</span>
      <span className={styles.legendDef}>
        Count of <code>slot_months_archive_chunks</code> manifest rows whose
        slot-id bounds fall entirely within this partition.
      </span>
      <span className={styles.legendTerm}>Rows</span>
      <span className={styles.legendDef}>
        Sum of <code>row_count</code> across this partition&apos;s chunks —
        approximate
        <code> evaluation_output_slot_months</code> rows archived so far.
      </span>
      <span className={styles.legendTerm}>Reached</span>
      <span className={styles.legendDef}>
        Highest <code>slot_id_max</code> in this partition&apos;s chunks. The
        next worker invocation will resume at <code>reached + 1</code>.
      </span>
      <span className={styles.legendTerm}>Last Upload</span>
      <span className={styles.legendDef}>
        Most recent <code>uploaded_at</code> timestamp for any chunk in this
        partition. Stale values indicate the partition has stalled or finished.
      </span>
      <span className={styles.legendTerm}>Running</span>
      <span className={styles.legendDef}>
        Green tag with elapsed time if a{' '}
        <code>Queued::ArchiveSlotMonthsRangeJob</code>
        is currently executing for this partition&apos;s slot-id range,
        &quot;Idle&quot; otherwise. Matched by exact <code>
          [min, max]
        </code>{' '}
        bounds.
      </span>
    </div>
  </div>
);

const ChunksLegend: FC = () => (
  <div className={styles.legend}>
    <div className={styles.legendGrid}>
      <span className={styles.legendTerm}>ID</span>
      <span className={styles.legendDef}>
        Primary key of the <code>slot_months_archive_chunks</code> row.
      </span>
      <span className={styles.legendTerm}>Year-Month</span>
      <span className={styles.legendDef}>
        <code>YYYY-MM</code> of the first eval&apos;s <code>created_at</code> in
        the chunk. Also the Azure blob path prefix.
      </span>
      <span className={styles.legendTerm}>Slot Min / Slot Max</span>
      <span className={styles.legendDef}>
        Lowest/highest <code>evaluation_output_slot_id</code> represented in
        this chunk. Used for resume cursor: a successor invocation starts at
        <code> Slot Max + 1</code>.
      </span>
      <span className={styles.legendTerm}>Eval Min / Eval Max</span>
      <span className={styles.legendDef}>
        Lowest/highest <code>evaluation_output_id</code> in the chunk — used by
        the reader to locate which chunk to load for a given eval.
      </span>
      <span className={styles.legendTerm}>Rows</span>
      <span className={styles.legendDef}>
        Count of <code>evaluation_output_slot_months</code> rows serialized into
        this Parquet chunk.
      </span>
      <span className={styles.legendTerm}>Status</span>
      <span className={styles.legendDef}>
        <strong>Verified</strong> when <code>verified_at</code> is set by
        <code> Archive::ChunkVerifier</code> (INS-8601). <strong>Failed</strong>{' '}
        when verification detected a mismatch — see Failure Reason column.
        <strong> Pending</strong> when the chunk exists but has not been
        verified yet.
      </span>
      <span className={styles.legendTerm}>Failure Reason</span>
      <span className={styles.legendDef}>
        Populated on a verification mismatch. <code>layer1: ...</code> = parquet
        bytes disagree with the manifest (upload corruption, truncation);{' '}
        <code>layer2: ...</code> = per-eval parquet SUM disagrees with the
        follower DB (possible dumper bug; only checked for evals not already
        drained by the old path).
      </span>
      <span className={styles.legendTerm}>Uploaded At</span>
      <span className={styles.legendDef}>
        When the Parquet file was successfully uploaded to Azure and the
        manifest row was created.
      </span>
      <span className={styles.legendTerm}>Verified At</span>
      <span className={styles.legendDef}>
        When <code>Archive::ChunkVerifier</code> last stamped a PASS on this
        chunk. Cleared if a later re-run fails.
      </span>
      <span className={styles.legendTerm}>Chunk Path</span>
      <span className={styles.legendDef}>
        Azure blob path —{' '}
        <code>
          {
            '{container}/archive/{year-month}/slot_{min}_{max}_chunk_{nnn}.parquet'
          }
        </code>
        . Collision-free across workers because of per-worker slot range.
      </span>
    </div>
  </div>
);

const STATIC_TABLE_CONFIG = {
  columnFilters: false,
  globalSearch: false,
  hideFooter: true,
} as const;

// ── Page Component ──────────────────────────────────────────────────────

export const BulkDumpStatusPage: FC = () => {
  const [loadable, refresh] = useAtom(bulkDumpStatusAtom);
  const chunks = usePaginatedChunks(CHUNKS_SORT_MAP);

  const handleRefresh = useCallback(() => {
    refresh();
    chunks.refresh();
  }, [refresh, chunks]);

  if (loadable.state === 'loading') {
    return (
      <>
        <PageHeader breadcrumbs={[]} title="Bulk Dump Status" actions={null} />
        <div className={styles.container}>Loading...</div>
      </>
    );
  }

  if (loadable.state === 'hasError') {
    return (
      <>
        <PageHeader breadcrumbs={[]} title="Bulk Dump Status" actions={null} />
        <InlineNotification
          kind="error"
          title="Failed to load bulk dump status"
          subtitle={String(loadable.error)}
        />
      </>
    );
  }

  const { overview, partitions, queueStats, busyDrainJobs, busyVerifyJobs } =
    loadable.data;

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Bulk Dump Status"
        actions={
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Renew}
            iconDescription="Refresh"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        }
      />
      <div className={styles.container}>
        <OverviewSection overview={overview} />
        <QueueSection queueStats={queueStats} />

        <div className={styles.section}>
          <h4>Partitions ({partitions.length})</h4>
          <PartitionsLegend />
          {partitions.length === 0 ? (
            <p className={styles.emptyState}>
              No partitions — cutoff not set or no slots exist.
            </p>
          ) : (
            <DataTable<BulkDumpPartition>
              data={partitions}
              columns={partitionColumns}
              getRowId={(row) => String(row.index)}
              config={STATIC_TABLE_CONFIG}
            />
          )}
        </div>

        <div className={styles.section}>
          <h4>Busy Drain Jobs ({busyDrainJobs.length})</h4>
          {busyDrainJobs.length === 0 ? (
            <p className={styles.emptyState}>
              No ArchiveSlotMonthsRangeJob currently running
            </p>
          ) : (
            <DataTable<BulkDumpBusyJob>
              data={busyDrainJobs}
              columns={busyColumns}
              getRowId={(row) =>
                `${row.slotIdMin}-${row.slotIdMax}-${row.process}`
              }
              config={STATIC_TABLE_CONFIG}
            />
          )}
        </div>

        <div className={styles.section}>
          <h4>Busy Verifier Jobs ({busyVerifyJobs.length})</h4>
          {busyVerifyJobs.length === 0 ? (
            <p className={styles.emptyState}>
              No VerifyArchiveChunksJob currently running
            </p>
          ) : (
            <DataTable<BulkDumpBusyJob>
              data={busyVerifyJobs}
              columns={busyColumns}
              getRowId={(row) =>
                `${row.slotIdMin}-${row.slotIdMax}-${row.process}`
              }
              config={STATIC_TABLE_CONFIG}
            />
          )}
        </div>

        <div className={styles.section}>
          <h4>Chunks Manifest ({fmt(chunks.totalCount)})</h4>
          <ChunksLegend />
          <DataTable<BulkDumpChunk>
            data={chunks.data}
            columns={chunkColumns}
            getRowId={(row) => String(row.id)}
            isLoading={chunks.isLoading}
            error={chunks.error}
            config={{}}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: chunks.totalCount,
            }}
            onPaginationChange={chunks.handleLoadData}
            onSortChange={(s) => chunks.handleSortChange(s.sorting)}
            onFilterChange={(s) => chunks.handleFilterChange(s.columnFilters)}
          />
        </div>
      </div>
    </>
  );
};
